import React, { useRef, useState } from "react";
import { styled, useTheme } from "@mui/material/styles";
import axiosInstance from "../api/axios";
import LinearProgress from '@mui/material/LinearProgress';
import {
  Box,
  Typography,
  TextField,
  Grid,
  Button,
  Paper,
  Snackbar,
  Alert,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import getApiErrorMessage from "../utils/getApiErrorMessage";

// Styled drop zone
const DropZone = styled(Paper)(({ theme, isdragover }) => ({
  border: "2px dashed #ccc",
  backgroundColor: isdragover ? "#f5f5f5" : "#fafafa",
  textAlign: "center",
  padding: theme.spacing(4),
  cursor: "pointer",
  position: "relative",
}));

const MAX_FILES = 3;

export default function PublicFormPage() {
  const fileInputRef = useRef(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [formValues, setFormValues] = useState({
    doctorName: "",
    patientFirst: "",
    patientLast: "",
    shade: "",
    returnDate: "",
    notes: "",
    pan: "",
  });
  const [files, setFiles] = useState([]);
  const [isDragOver, setIsDragOver] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === "dark";
  const showUploadProgress = submitting && files.length > 0;

  const handleSnackbarClose = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormValues((prev) => ({ ...prev, [name]: value }));
  };

  const addFilesWithLimit = (incoming = []) => {
    let trimmedCount = 0;
    setFiles((prev) => {
      const deduped = [...prev, ...incoming].filter(
        (f, i, arr) =>
          i === arr.findIndex((x) => x.name === f.name && x.size === f.size)
      );
      if (deduped.length > MAX_FILES) {
        trimmedCount = deduped.length - MAX_FILES;
        return deduped.slice(0, MAX_FILES);
      }
      return deduped;
    });
    if (trimmedCount > 0) {
      setSnackbar({
        open: true,
        message: `Maximum ${MAX_FILES} files allowed. Ignored ${trimmedCount} extra file(s).`,
        severity: "warning",
      });
    }
  };

  const handleFileChange = (e) => {
    const selected = Array.from(e.target.files);
    addFilesWithLimit(selected);
    e.target.value = null;
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const dropped = Array.from(e.dataTransfer.files);
    addFilesWithLimit(dropped);
    setIsDragOver(false);
  };

  const handleRemoveFile = (index) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = () => {
    setIsDragOver(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    // Validate required fields
    const { doctorName, patientFirst, patientLast, shade, returnDate } =
      formValues;
    if (!doctorName || !patientFirst || !patientLast || !shade || !returnDate) {
      setSnackbar({
        open: true,
        message: "Please fill all required fields.",
        severity: "error",
      });
      return;
    }

    setUploadProgress(0);
    setSubmitting(true);
    try {
      const data = new FormData();

      // 1️⃣ Convert returnDate (YYYY-MM-DD) into full ISO
      if (formValues.returnDate) {
        const iso = new Date(formValues.returnDate).toISOString();
        data.append("returnDate", iso);
      }
 
      // 2️⃣ Append all the other simple fields except returnDate
      Object.entries(formValues).forEach(([key, value]) => {
        if (key === "returnDate") return;
        if (value) data.append(key, value);
      });


    files.forEach(file => data.append('attachments', file));

     await axiosInstance.post('/forms', data, {
  onUploadProgress: (progressEvent) => {
    if (!progressEvent.total) return;
    const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
    setUploadProgress(Math.min(100, Math.max(0, percent)));
  },
});

 setSnackbar({
        open: true,
        message: "Form submitted successfully!",
        severity: "success",
      });
      // Reset form
      setFormValues({
        doctorName: "",
        patientFirst: "",
        patientLast: "",
        shade: "",
        returnDate: "",
        notes: "",
        pan: "",
      });
      setFiles([]);
    } catch (err) {
      setSnackbar({
        open: true,
        message: getApiErrorMessage(err, "Submission failed. Please try again."),
        severity: "error",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box display="flex" justifyContent="center" p={10} sx={{ backgroundColor: '#f7fafd'}}>
      <Paper
        elevation={3}
        sx={{ width: "100%", maxWidth: 800, borderRadius: 2, p: 4, backgroundColor: '#ffffff', }}
      >
        <Typography variant="h5" gutterBottom>
          Send File
        </Typography>

        <Box component="form" onSubmit={handleSubmit} noValidate>
          <Grid container spacing={3}>
            {/* Doctor */}

            <Grid size={12}>
              <TextField
                label="Doctor"
                name="doctorName"
                required
                fullWidth
                placeholder="Full Name"
                value={formValues.doctorName}
                onChange={handleInputChange}
              />
            </Grid>

            {/* Patient First & Last */}
            <Grid size={6}>
              <TextField
                label="Patient First Name"
                name="patientFirst"
                required
                fullWidth
                placeholder="First Name"
                value={formValues.patientFirst}
                onChange={handleInputChange}
              />
            </Grid>
            <Grid size={6}>
              <TextField
                label="Patient Last Name"
                name="patientLast"
                required
                fullWidth
                placeholder="Last Name"
                value={formValues.patientLast}
                onChange={handleInputChange}
              />
            </Grid>

            {/* Shade & Return Date */}
            <Grid size={6}>
              <TextField
                label="Shade"
                name="shade"
                required
                fullWidth
                placeholder="Shade"
                value={formValues.shade}
                onChange={handleInputChange}
              />
            </Grid>
            <Grid size={6}>
              <TextField
                label="Return Date"
                name="returnDate"
                type="date"
                required
                fullWidth
                InputLabelProps={{ shrink: true }}
                value={formValues.returnDate}
                onChange={handleInputChange}
              />
            </Grid>

            {/* Notes */}
            <Grid size={12}>
              <TextField
                label="Notes"
                name="notes"
                fullWidth
                multiline
                rows={4}
                placeholder="Any notes"
                value={formValues.notes}
                onChange={handleInputChange}
              />
            </Grid>

            {/* File Upload */}
            <Grid size={12}>
              <DropZone
                elevation={0}
                isdragover={isDragOver ? 1 : 0}
                onClick={() => fileInputRef.current.click()}
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                sx={{
                 backgroundColor: isDarkMode
                     ? theme.palette.background.paper
                     : theme.palette.background.default,
                   border: `2px dashed ${theme.palette.divider}`,
                   color: theme.palette.text.primary,
                   p: 2,
                   textAlign: "center",
                   cursor: "pointer",
                   "&:hover": {
                     backgroundColor: isDarkMode
                       ? theme.palette.grey[800]
                       : theme.palette.grey[200],
                   },
                 }}
               >
                <Typography>
                  Drag and drop files here or click to select (max {MAX_FILES})
                </Typography>
                <Typography variant="caption">(Max files size: 1 GB)</Typography>
                <input
                  ref={fileInputRef}
                  type="file"
                  hidden
                  multiple
                  onChange={handleFileChange}
                />
              </DropZone>

              {files.length > 0 && (
                <Box mt={1}>
                  {files.map((f, i) => (
                    <Box
                      key={`${f.name}-${f.size}-${i}`}
                      display="flex"
                      alignItems="center"
                      justifyContent="space-between"
                      sx={{ borderBottom: 1, borderColor: "divider", py: 0.5 }}
                    >
                      <Typography variant="body2">• {f.name}</Typography>
                      <IconButton
                        size="small"
                        aria-label={`Remove ${f.name}`}
                        onClick={() => handleRemoveFile(i)}
                      >
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  ))}
                </Box>
              )}
            </Grid>

            <Grid size={12}>
              {showUploadProgress && (
                <Box mb={2}>
                  <Typography variant="body2" align="center">
                    Uploading: {uploadProgress}%
                  </Typography>
                  <LinearProgress variant="determinate" value={uploadProgress} />
                </Box>
              )}
              <Button
                type="submit"
                variant="contained"
                fullWidth
                disabled={submitting}
                sx={{
                   // again use theme tokens
                   backgroundColor: theme.palette.primary.main,
                   "&:hover": {
                     backgroundColor: theme.palette.primary.dark,
                   },
                   color: theme.palette.primary.contrastText,
                 }}
               >
                {submitting ? `Uploading ${uploadProgress}%` : "Submit"}
              </Button>
            </Grid>
          </Grid>
        </Box>

{/* 2) Embed code generator */}
        {/* <Box mt={4}>
          <Typography variant="h6" gutterBottom>
            Embed this form in your site
          </Typography>
          <TextField
            fullWidth
            multiline
            minRows={2}
            value={embedCode}
            InputProps={{
              readOnly: true,
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    edge="end"
                    onClick={() => {
                      navigator.clipboard.writeText(embedCode);
                      setSnackbar({ open: true, message: "Copied!", severity: "success" });
                    }}
                  >
                    <ContentCopyIcon />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </Box> */}


        {/* Snackbar notifications */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={handleSnackbarClose}
        >
          <Alert
            onClose={handleSnackbarClose}
            severity={snackbar.severity}
            sx={{ width: "100%" }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Paper>
    </Box>
  );
}
